import { Button, Flex, Space, Table, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteSite } from "../../entities/api";
import { useFetchSitesPagination } from "../../entities/api/useSitesAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import SearchFilter from "~components/SearchFilter/SearchFilter.js";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { ExportModal } from "~components/ExportModal/ExportModal.tsx";
import { defaultSitesExportFieldNames, sitesExportFields } from "~pages/Sites/constants/exportFields.ts";
import { useExportSites } from "~pages/Sites/hooks/useExportSites.ts";
import { useImportSites } from "~pages/Sites/hooks/useImportSites.ts";

const PermissionName = PermissionNames.SITE;

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "country",
        defaultMessage: "Country",
      }),
      dataIndex: "countryCode",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.UPDATE ]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/sites/manage/${record.siteId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.DELETE ]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const SitesList = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);
  const [ selected, setSelected ] = useState({});
  const [ showConfirmation, setShowConfirmation ] = useState(false);
  const [ filteredData, setFilteredData ] = useState([]);
  const { mutate: deleteFn, isPending } = useDeleteSite();
  const [ searchText, setSearchText ] = useState("");
  const { data: sitesData, isLoading } = useFetchSitesPagination({
    limit: pageSize,
    offset,
    ...(searchText && { nameLocalContains: searchText }),
  });
  const { mutateAsync: startImporting } = useImportSites();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSites();

  const data = useMemo(() => sitesData?.data || [], [ sitesData?.data ]);
  const nextLink = useMemo(() => sitesData?.links?.next, [ sitesData?.links?.next ]);

  useEffect(() => {
    setFilteredData(data);
  }, [ data, setFilteredData ]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [ setSelected, setShowConfirmation ],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.siteId);
  }, [ deleteFn, selected?.siteId ]);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.DELETE ]}>
          <ConfirmationModal
            title={formatMessage({ id: 'site.delete' })}
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText={formatMessage({ id: 'delete' })}
          >
            <div>
              {formatMessage(
                {
                  id: "delete.confirm",
                },
                { item: selected.nameLocal },
              )}
            </div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>{formatMessage({ id: 'sites' })}</Typography.Title>
        <Space>
          <SearchFilter
            doSearch={setSearchText}
            placeholderIntlKey="search.two.letters"
          />
          {/*TODO: Uncomment after release*/}
          {/*<PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE, PermissionOperations.UPDATE ]}>*/}
          {/*  <ImportModal*/}
          {/*    title={formatMessage({ id: "import.modal.title.sites" })}*/}
          {/*    description={formatMessage({ id: "import.modal.text.sites" })}*/}
          {/*    startImporting={startImporting}*/}
          {/*  />*/}
          {/*</PermissionCheck>*/}
          {/*<PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.READ ]}>*/}
          {/*  <ExportModal*/}
          {/*    handleExport={startExport}*/}
          {/*    defaultFields={defaultSitesExportFieldNames}*/}
          {/*    groupedFields={sitesExportFields}*/}
          {/*    localStorageKey="export.sites"*/}
          {/*    loading={isExportPending}*/}
          {/*  />*/}
          {/*</PermissionCheck>*/}
          <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE ]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/sites/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                {formatMessage({ id: "site" })}
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey={"siteId"}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + data.length + 1 : data.length + offset,
          position: [ "bottomCenter" ],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
      />
    </Flex>
  );
};

export default SitesList;
